import React, { memo } from 'react';
import styled from 'styled-components';

import { TICKET_TYPES } from '../../../../constants';
import { TITLES } from './constants';
import { ValueOf } from '@utils/tsUtils';

export const TYPES = TICKET_TYPES;

export interface ITicketType {
  className?: string;
  id?: string;
  disabled?: boolean;
  subtype?: string;
  type: ValueOf<typeof TYPES>;
  suffix?: React.ReactNode;
}

const TicketType = ({
  disabled: isDisabled,
  subtype,
  type,
  id,
  suffix,
  ...props
}: ITicketType) => {
  const typeTitle = TITLES[type];
  const subtypeTitle = subtype ? TITLES[subtype] : null;

  const label =
    (id && `Тикет ${id}`) || (type !== TYPES.UNKNOWN && subtypeTitle);

  return (
    <StyledContainer {...props} disabled={isDisabled} type={type}>
      <StyledType disabled={isDisabled} type={type}>
        {typeTitle}
      </StyledType>
      {label && <StyledLabel>{label}</StyledLabel>}
      {suffix}
    </StyledContainer>
  );
};

export const StyledType = styled.div<{
  type: ITicketType['type'];
  disabled: ITicketType['disabled'];
}>`
  border-radius: 23px;
  color: #fff;
  height: 100%;
  height: 23px;
  line-height: 22px;
  overflow: hidden;
  padding: 0 10px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 103px;

  ${({ type }) =>
    type === TYPES.UPDATING &&
    `
    background-image: linear-gradient(to right, #35dad8, #1b58e2);
  `}

  ${({ type }) =>
    type === TYPES.TOUR_SELECTION &&
    `
    background-image: linear-gradient(to left, #dd3d3d, #a521ab);
  `}
  
  ${({ type }) =>
    type === TYPES.COMMON_QUESTION &&
    `
    background-image: linear-gradient(to left, #008bcf, #9e4de3);
  `}

  ${({ type }) =>
    type === TYPES.HOTLINE &&
    `
    background-image: linear-gradient(270deg, #DD9D3D 0%, #F01C0E 100%);
  `}
  
  ${({ type }) =>
    type === TYPES.UNKNOWN &&
    `
    background-color: #fff;
    color: #919499;
    line-height: 20px;
  `}
  
  ${({ disabled, type }) =>
    disabled &&
    type !== TYPES.UNKNOWN &&
    `
    background-color: #919499;
    background-image: none;
  `}
`;

const StyledLabel = styled.div`
  line-height: 22px;
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledContainer = styled.div<{
  type: ITicketType['type'];
  disabled: ITicketType['disabled'];
}>`
  align-items: center;
  border-radius: 23px;
  display: flex;
  font-size: 14px;
  height: 23px;
  width: 100%;

  ${({ type }) =>
    type === TYPES.UPDATING &&
    `
    background-image: linear-gradient(to right, #e5f4fb, #e3ebfc);
  `}

  ${({ type }) =>
    type === TYPES.TOUR_SELECTION &&
    `
    background-image: linear-gradient(to left, #fbe7e8, #f7e5f0);
  `}

  ${({ type }) =>
    type === TYPES.COMMON_QUESTION &&
    `
    background-image: linear-gradient(to left, #e1f0fa, #ebecfb);
  `}

  ${({ type }) =>
    type === TYPES.HOTLINE &&
    `
    background-image: linear-gradient(270deg, #FBF7E7 0%, #F7E5E5 61.33%);
  `}

  ${({ type }) =>
    type === TYPES.UNKNOWN &&
    `
      flex: 1;
      border: 1px solid #919499
      ${StyledType} {
        color: white;
        background: #919499;
      }`}

  ${({ disabled, type }) =>
    disabled &&
    type !== TYPES.UNKNOWN &&
    `
    background-color: #d7d7d7;
    background-image: none;
  `}
`;

export default memo(TicketType);
